import {
  clearFailureLogged,
  clearLoginRedirectTimestamp,
  eraseCookie,
  isFailureLogged,
  isInIFrame,
  logger,
  parseCookie,
  readCookie,
  setFailureLogged,
  setLoginRedirectTimestamp,
  wasInitiatingSession,
} from '../utils';

export const AuthLogMessage = {
  LOGIN_FAILED: '[auth] Login Failed',
  UNAUTHENTICATED: '[auth] Unauthenticated',
  SESSION_EXPIRED: '[auth] Session expired',
  LOGIN_SUCCESSFUL: '[auth] Login Successful',
  AUTHENTICATED: '[auth] Authenticated',
  IFRAME_AUTH: '[auth] iFrame auth',
  UNAUTHORIZED: '[auth] Unauthorized',
} as const;

export const AuthAction = {
  LOGIN: 'login',
  LOGOUT: 'logout',
  AUTHENTICATE: 'authenticate',
  NONE: 'none',
} as const;

export type AuthAction = (typeof AuthAction)[keyof typeof AuthAction];

export interface AuthSession {
  expires_at: number;
}

export interface GetAuthActionParams {
  sessionCookieName: string;
  /** Optional session for testing */
  testSession?: AuthSession;
}

/**
 * Determines the auth action based on current state.
 * Reads cookies, checks expiration, detects iframe, reads path.
 * Handles logging and localStorage side effects.
 * Unit testable by mocking localStorage, cookies, window.location, and logger.
 */
export function getAuthAction({ sessionCookieName, testSession }: GetAuthActionParams): AuthAction {
  const cookieValue = readCookie(sessionCookieName);
  const hasCookie = cookieValue != null || testSession != null;
  const { isExpired, expiresAt } = checkSessionCookieExpiration(sessionCookieName, testSession);
  const inIFrame = isInIFrame();
  const onUnauthorizedPath = window.location.pathname === '/unauthorized';
  const recentlyRedirectedToLogin = wasInitiatingSession();

  const extra = { sessionCookieName, expiresAt };

  if (!hasCookie) {
    // No session cookie
    if (inIFrame) {
      logger.info(AuthLogMessage.IFRAME_AUTH, { extra });
      return AuthAction.NONE;
    }
    if (onUnauthorizedPath) {
      logger.info(AuthLogMessage.UNAUTHORIZED, { extra });
      return AuthAction.NONE;
    }

    if (recentlyRedirectedToLogin && !isFailureLogged()) {
      // User was redirected to login within the last minute, came back without a cookie
      setFailureLogged();
      logger.info(AuthLogMessage.LOGIN_FAILED, { extra });
    } else {
      // Normal first visit, or continuous retry (failure already logged)
      logger.info(AuthLogMessage.UNAUTHENTICATED, { extra });
    }

    // Clear failure flag when starting fresh (timestamp stale)
    if (!recentlyRedirectedToLogin) {
      clearFailureLogged();
    }
    setLoginRedirectTimestamp();
    return AuthAction.LOGIN;
  }

  // Has session cookie
  if (isExpired) {
    logger.info(AuthLogMessage.SESSION_EXPIRED, { extra });
    if (inIFrame) {
      return AuthAction.NONE;
    }
    eraseCookie(sessionCookieName);
  } else {
    const logMessage = recentlyRedirectedToLogin ? AuthLogMessage.LOGIN_SUCCESSFUL : AuthLogMessage.AUTHENTICATED;
    logger.info(logMessage, { extra });
  }

  // Clear login tracking for authenticated or logout paths
  clearLoginRedirectTimestamp();
  clearFailureLogged();
  return isExpired ? AuthAction.LOGOUT : AuthAction.AUTHENTICATE;
}

/**
 * Check if the session cookie is expired, assumes expires_at cookie is in seconds and will multiply by 1000.
 * Session expiry date is expected to be in UTC.
 */
export function checkSessionCookieExpiration(
  sessionCookieName: string,
  testSession?: AuthSession
): {
  isExpired: boolean;
  expiresAt?: number;
} {
  const cookieValue = readCookie(sessionCookieName);
  if (cookieValue == null && !testSession) {
    return { isExpired: true };
  }
  const session: AuthSession | null = testSession ?? parseCookie(cookieValue);
  if (session == null) {
    return { isExpired: true };
  }
  const expiresAt = session.expires_at * 1000;
  const isExpired = expiresAt < Date.now();
  if (isExpired) {
    return { isExpired: true, expiresAt };
  }
  return { isExpired: false, expiresAt };
}
